import { motion, AnimatePresence } from 'framer-motion';
import React, { useState, useEffect } from 'react';
import { AddFeedbackButton } from './feedback/AddFeedbackButton';
import { FeedbackModal } from './feedback/FeedbackModal';
import { FeedbackList } from './feedback/FeedbackList';
import { Feedback } from '@/types/feedback';
import { AnimatedText } from './HeroAnimationText';
import { feedbackService } from '@/services/feedbackService';
import { ChevronDown, ChevronUp } from 'lucide-react';

export const HeroSection: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const loadFeedbacks = async () => {
    try {
      setIsLoading(true);
      const data = await feedbackService.getAll();
      console.log('Fetched feedbacks:', data);
      setFeedbacks(data);
    } catch (error) {
      console.error('Error loading feedbacks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFeedbacks();
  }, []);

  const handleSubmitFeedback = async (
    newFeedback: Omit<Feedback, 'id' | 'createdAt'>
  ) => {
    try {
      const created = await feedbackService.create(newFeedback);
      console.log('Created feedback:', created);
      await loadFeedbacks();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="py-2 px-3 sm:px-6 mt-4 sm:mt-6">
        <div className="max-w-6xl mx-auto">
          {/* Main Hero Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 sm:gap-2 items-center">
            {/* Profile Picture - Always Visible */}
            <motion.div
              className="flex justify-center order-1"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="relative w-40 h-52 sm:w-48 sm:h-60">
                <img
                  src={
                    new URL(
                      '../../assets/images//df2ee03c-5722-4e69-9528-722bfbaad0ff.JPG',
                      import.meta.url
                    ).href
                  }
                  alt="Jacob Joseph- Mobile App Developer"
                  className="rounded-lg w-full h-full object-cover border-4 border-purple-200 shadow-lg transform transition-transform hover:scale-105"
                />
              </div>
            </motion.div>

            {/* Basic Info - Always Visible */}
            <div className="text-center sm:text-left order-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="mb-2">
                  <span className="animate-wave text-lg sm:text-xl">👋</span>
                  <span className="ml-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
                    Hi, I'm
                  </span>
                </div>

                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-gray-800 dark:text-gray-100">
                  Yusufu
                </h1>
                <h2 className="text-xs sm:text-sm md:text-lg font-bold mb-4 leading-tight">
                  <AnimatedText
                    text="Specialized in Mobile App Development"
                    isWave
                    letterSpacing="0.02em sm:0.03em"
                    isLongText
                  />
                </h2>

                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4">
                  Full Stack Mobile App Developer with a passion for creating
                  robust and user-friendly applications.
                </p>

                {/* Expand Button */}
                <div className="flex justify-center">
                  <motion.button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm 
                             bg-gradient-to-r from-purple-600 to-blue-500 text-white 
                             rounded-lg font-medium hover:from-purple-700 hover:to-blue-600 
                             focus:outline-none focus:ring-2 focus:ring-purple-500 
                             focus:ring-offset-2 transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {isExpanded ? 'Show Less' : 'Expertise & Skills'}
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Expandable Content */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="mt-8 md:grid-cols-2 gap-6 p-6 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl">
                  {/* Skills Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">
                      Expertise & Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {[
                        'Mobile App Development',
                        'Web App Development',
                        'QA Testing',
                        'React Native',
                        'Flutter',
                        'iOS',
                        'Android',
                        'Typescript',
                        'Firebase',
                        'Nodejs + express',
                        'MongoDB',
                        'SQLITE3',
                        'Rest API',
                        'UI/UX',
                      ].map(skill => (
                        <span
                          key={skill}
                          className="px-3 py-1 text-sm bg-purple-100 text-purple-800 
                                   rounded-full dark:bg-purple-900 dark:text-purple-100 
                                   whitespace-nowrap"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                      Experienced in full development lifecycle and quality
                      assurance, ensuring robust and user-friendly applications
                      that meet the highest standards.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Feedback Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="py-2 mt-6 bg-gray-50 dark:bg-gray-900"
      >
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-4">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
            </div>
          ) : (
            <>
              {feedbacks.length > 0 ? (
                <FeedbackList
                  feedbacks={feedbacks}
                  onAddFeedback={() => setIsModalOpen(true)}
                />
              ) : (
                <div className="text-center py-20 text-gray-500">
                  <p className="mb-4">
                    No feedbacks yet. Be the first to leave feedback!
                  </p>
                  <AddFeedbackButton onClick={() => setIsModalOpen(true)} />
                </div>
              )}
            </>
          )}
        </div>
      </motion.section>

      <FeedbackModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmitFeedback}
      />
    </>
  );
};
